// 📄 pages/Proyectos/components/IncidenteErrorConocidoLink.tsx
// Bloque de vinculación de un Incidente a un registro del catálogo de
// Errores Conocidos (ver sección "Resolución" en IncidenteDetailModal.tsx).
// Dos estados mutuamente excluyentes según `errorConocidoRef`:
//   A) sin vínculo — buscador sobre el catálogo (ya cargado en memoria por
//      el padre) + fallback de nota SAP manual si no aparece nada.
//   B) con vínculo — card compacta con acceso al detalle del error y
//      opción de desvincular (con confirmación).
import { useMemo, useState } from 'react';
import { Search, ExternalLink, Save, RefreshCw, Link2Off } from 'lucide-react';
import {
  KnownError, matchesSearch, sistemaAfectadoMeta, resolveNotaSapUrl
} from '../../../services/knownErrorsService.ts';
import { KnownErrorRef } from '../../../services/incidenteService.ts';
import KnownErrorDetailModal from '../../Ariba/components/KnownErrorDetailModal.tsx';
import ConfirmDialog from '../../../components/ui/ConfirmDialog.tsx';

interface IncidenteErrorConocidoLinkProps {
  knownErrors: KnownError[];
  errorConocidoRef: KnownErrorRef | null;
  notaSapManual: string;
  disabled?: boolean;
  onLink: (ref: KnownErrorRef) => Promise<void>;
  onUnlink: () => Promise<void>;
  onSaveNotaSapManual: (value: string) => Promise<void>;
}

export default function IncidenteErrorConocidoLink({
  knownErrors, errorConocidoRef, notaSapManual, disabled,
  onLink, onUnlink, onSaveNotaSapManual
}: IncidenteErrorConocidoLinkProps) {
  const [busqueda, setBusqueda] = useState('');
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [confirmarDesvincular, setConfirmarDesvincular] = useState(false);
  const [verDetalle, setVerDetalle] = useState(false);
  const [notaSapInput, setNotaSapInput] = useState(notaSapManual);
  const [savingNota, setSavingNota] = useState(false);

  const resultados = useMemo(() => {
    if (!busqueda.trim()) return [];
    return knownErrors.filter(e => matchesSearch(e, busqueda)).slice(0, 8);
  }, [knownErrors, busqueda]);

  const errorVinculado = errorConocidoRef ? knownErrors.find(e => e.id === errorConocidoRef.id) : null;

  const handleSeleccionar = async (error: KnownError) => {
    setLinking(true);
    try {
      await onLink({ id: error.id, code: error.code, title: error.title });
      setBusqueda('');
    } finally {
      setLinking(false);
    }
  };

  const handleDesvincular = async () => {
    setUnlinking(true);
    try {
      await onUnlink();
      setConfirmarDesvincular(false);
    } finally {
      setUnlinking(false);
    }
  };

  const handleGuardarNota = async () => {
    setSavingNota(true);
    try {
      await onSaveNotaSapManual(notaSapInput);
    } finally {
      setSavingNota(false);
    }
  };

  const notaSapManualLink = resolveNotaSapUrl(notaSapManual);

  if (errorConocidoRef) {
    // Estado B: con referencia vinculada.
    const sistema = errorVinculado ? sistemaAfectadoMeta(errorVinculado.sistemaAfectado) : null;
    const notaLinkVinculado = errorVinculado ? resolveNotaSapUrl(errorVinculado.notaSapUrl) : null;
    return (
      <div className="bg-white/5 border border-white/10 rounded-xl p-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <span className="text-xs font-mono font-semibold text-blue-400">{errorConocidoRef.code}</span>
            <span className="text-sm text-white truncate">{errorConocidoRef.title}</span>
            {sistema && (
              <span className="text-[10px] bg-sky-500/10 text-sky-300 border border-sky-500/30 px-2 py-0.5 rounded-full flex-shrink-0">{sistema.label}</span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {errorVinculado && (
              <button
                type="button"
                onClick={() => setVerDetalle(true)}
                className="text-xs text-blue-400 hover:text-blue-300 font-medium"
              >
                Ver detalle
              </button>
            )}
            {!disabled && (
              <button
                type="button"
                onClick={() => setConfirmarDesvincular(true)}
                disabled={unlinking}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-400 disabled:opacity-50"
                title="Desvincular"
              >
                <Link2Off className="w-3.5 h-3.5" /> Desvincular
              </button>
            )}
          </div>
        </div>
        {notaLinkVinculado && (
          <a
            href={notaLinkVinculado}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 mt-2"
          >
            Abrir nota SAP <ExternalLink className="w-3 h-3" />
          </a>
        )}

        {verDetalle && errorVinculado && (
          <KnownErrorDetailModal error={errorVinculado} onClose={() => setVerDetalle(false)} />
        )}
        {confirmarDesvincular && (
          <ConfirmDialog
            title="¿Desvincular este error conocido?"
            message="El incidente dejará de estar vinculado al catálogo de Errores Conocidos. Podés volver a buscarlo y vincularlo más adelante."
            confirmLabel={unlinking ? 'Desvinculando...' : 'Desvincular'}
            danger
            onConfirm={handleDesvincular}
            onCancel={() => setConfirmarDesvincular(false)}
          />
        )}
      </div>
    );
  }

  // Estado A: sin referencia — buscador + fallback de nota SAP manual.
  return (
    <div className="space-y-2">
      <div>
        <label className="block text-[11px] text-gray-500 mb-1">¿Existe un error documentado relacionado?</label>
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            disabled={disabled || linking}
            placeholder="Buscar por código o título en Errores Conocidos..."
            className="w-full bg-zinc-800 border border-white/10 rounded-lg pl-8 pr-3 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500 disabled:opacity-50"
          />
        </div>

        {busqueda.trim() && (
          resultados.length > 0 ? (
            <div className="mt-1.5 border border-white/10 rounded-lg overflow-hidden divide-y divide-white/10">
              {resultados.map((error) => {
                const sistema = sistemaAfectadoMeta(error.sistemaAfectado);
                return (
                  <button
                    key={error.id}
                    type="button"
                    onClick={() => handleSeleccionar(error)}
                    disabled={linking}
                    className="w-full flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/10 text-left transition-colors disabled:opacity-50"
                  >
                    <span className="text-xs font-mono font-semibold text-blue-400 flex-shrink-0">{error.code}</span>
                    <span className="text-xs text-gray-300 truncate flex-1">{error.title}</span>
                    {sistema && (
                      <span className="text-[10px] bg-sky-500/10 text-sky-300 border border-sky-500/30 px-2 py-0.5 rounded-full flex-shrink-0">{sistema.label}</span>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-[11px] text-gray-500 mt-1.5">No encontrado en el catálogo. Podés registrar la nota SAP manualmente abajo.</p>
          )
        )}
        {linking && <p className="text-[11px] text-blue-400 mt-1.5 flex items-center gap-1.5"><RefreshCw className="w-3 h-3 animate-spin" /> Vinculando...</p>}
      </div>

      <div>
        <label className="block text-[11px] text-gray-500 mb-1">Nota SAP (número o URL) — registro manual</label>
        <div className="flex items-center gap-1.5">
          <input
            type="text"
            value={notaSapInput}
            onChange={(e) => setNotaSapInput(e.target.value)}
            disabled={disabled}
            placeholder="ej. 3749732"
            className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500 disabled:opacity-50"
          />
          {!disabled && notaSapInput !== notaSapManual && (
            <button
              type="button"
              onClick={handleGuardarNota}
              disabled={savingNota}
              title="Guardar nota SAP"
              className="flex-shrink-0 p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
            >
              {savingNota ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            </button>
          )}
          {notaSapManualLink && (
            <a
              href={notaSapManualLink}
              target="_blank"
              rel="noopener noreferrer"
              title="Abrir nota"
              className="flex-shrink-0 p-1.5 text-gray-400 hover:text-blue-400 hover:bg-white/10 rounded-lg transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
